import React from 'react';
import { Box, Button, Paper, Stack, Typography, Alert } from '@mui/material';
import { ArrowBack, Refresh, BugReport, OpenInNew } from '@mui/icons-material';

type K9ErrorFallbackProps = {
  title?: string;
  message?: string;
  error?: Error | null;
  onRetry?: () => void;
};

const QUICK_MENU_PATH = '/k9-ops/quick-menu';
const DIAGNOSTICS_PATH = '/k9-ops/diagnostics';

export default function K9ErrorFallback(props: K9ErrorFallbackProps) {
  const {
    title = 'Something went wrong',
    message = 'The page failed to load. You can retry, go back to the Quick Menu, or open Diagnostics.',
    error,
    onRetry,
  } = props;

  const goQuickMenu = () => {
    window.location.href = QUICK_MENU_PATH;
  };

  const goDiagnostics = () => {
    window.location.href = DIAGNOSTICS_PATH;
  };

  return (
    <Box sx={{ px: 2, py: 3 }}>
      <Paper
        elevation={3}
        sx={{
          p: 2.5,
          borderRadius: 2,
          maxWidth: 560,
          mx: 'auto',
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>

          {import.meta.env.DEV && error?.message && (
            <Alert severity="info">
              <Typography
                variant="caption"
                component="pre"
                sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {error.message}
              </Typography>
            </Alert>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              onClick={onRetry ?? (() => window.location.reload())}
              startIcon={<Refresh />}
            >
              Retry
            </Button>
            <Button variant="outlined" onClick={goQuickMenu} startIcon={<ArrowBack />}>
              Quick Menu
            </Button>
            <Button variant="outlined" onClick={goDiagnostics} startIcon={<BugReport />}>
              Diagnostics
            </Button>
            <Button
              variant="text"
              href="/k9-ops/dashboard"
              target="_blank"
              rel="noreferrer"
              startIcon={<OpenInNew />}
            >
              Dashboard
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

